import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { DarkModeProvider } from './context/DarkModeContext';
import { BrandingProvider } from './context/BrandingContext';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './components/Login';
import Dashboard from './pages/Dashboard';
import Companies from './pages/Companies';
import CompanyDetails from './pages/CompanyDetails';
import Services from './pages/Services';
import Tickets from './pages/Tickets';
import TicketDetail from './pages/TicketDetail';
import Users from './pages/Users';
import AcceptInvite from './pages/AcceptInvite';
import ResetPassword from './pages/ResetPassword';
import Settings from './pages/Settings';
import SsoSuccess from './pages/SsoSuccess';
import Bookmarks from './pages/Bookmarks';
import Wiki from './pages/Wiki';
import InternalProjects from './pages/InternalProjects';
import Feedback from './pages/Feedback';
import PortalIssues from './pages/PortalIssues';
import MyFeedback from './pages/MyFeedback';

const AppRoutes: React.FC = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand"></div>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={!user ? <Login /> : <Navigate to="/" replace />} />
      <Route path="/accept-invite" element={!user ? <AcceptInvite /> : <Navigate to="/" replace />} />
      <Route path="/reset-password" element={!user ? <ResetPassword /> : <Navigate to="/" replace />} />
      <Route path="/sso-success" element={<SsoSuccess />} />
      <Route path="/sms-consent" element={<Navigate to="/api/sms-consent" replace />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout>
              <Dashboard />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/companies"
        element={
          <ProtectedRoute requireGlobalAdmin>
            <Layout>
              <Companies />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/companies/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <CompanyDetails />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <ProtectedRoute requireGlobalAdmin>
            <Layout>
              <Users />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/services"
        element={
          <ProtectedRoute>
            <Layout>
              <Services />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/tickets"
        element={
          <ProtectedRoute>
            <Layout>
              <Tickets />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/tickets/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <TicketDetail />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Layout>
              <Settings />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/bookmarks"
        element={
          <ProtectedRoute>
            <Layout>
              <Bookmarks />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/wiki"
        element={
          <ProtectedRoute>
            <Layout>
              <Wiki />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/internal/projects"
        element={
          <ProtectedRoute requireGlobalAdmin>
            <Layout>
              <InternalProjects />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/internal/projects/:projectId"
        element={
          <ProtectedRoute requireGlobalAdmin>
            <Layout>
              <InternalProjects />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/feedback"
        element={
          <ProtectedRoute>
            <Layout>
              <Feedback />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-feedback"
        element={
          <ProtectedRoute>
            <Layout>
              <MyFeedback />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/portal-issues"
        element={
          <ProtectedRoute requireGlobalUser>
            <Layout>
              <PortalIssues />
            </Layout>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

function App() {
  return (
    <Router>
      <DarkModeProvider>
        <BrandingProvider>
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        </BrandingProvider>
      </DarkModeProvider>
    </Router>
  );
}

export default App;
